import {
  Box,
  type BoxProps,
  Image as ChakraImage,
  Flex,
  HStack,
  Text,
  useBreakpointValue,
  useMediaQuery
} from '@chakra-ui/react'
import React from 'react'
import { useLocation } from 'react-router-dom'
import EncryptionImage from '../../../images/badges/encryption.svg'
import HIPAAImage from '../../../images/badges/hipaa.svg'
import PHIPAImage from '../../../images/badges/phipa.svg'
import SOC2Image from '../../../images/badges/soc2.svg'

const FacePileImageDesktop =
  'https://res.cloudinary.com/hellojoy/image/upload/v1739997090/onboarding/blueprint-reviews-compress.webp'
const FacePileImageMobile =
  'https://res.cloudinary.com/hellojoy/image/upload/v1739997090/onboarding/blueprint-reviews-m-compress.webp'

interface SocialProofFooterProps extends BoxProps {
  showTerms?: boolean
  termsComponent?: React.ReactNode
  minCollapsableHeight?: string
}

export const SocialProofFooter: React.FC<SocialProofFooterProps> = ({
  showTerms = false,
  termsComponent = null,
  minCollapsableHeight = '700px',
  ...boxProps
}) => {
  const location = useLocation()
  const isExtension = location.pathname.includes('/extension')
  const isWidget = location.pathname.includes('/widget')
  const isMiniWidget = location.pathname.includes('/mini-widget')

  const facePileSrc = useBreakpointValue({
    base: FacePileImageMobile,
    md: FacePileImageDesktop
  })
  const [tallEnoughForFacepile] = useMediaQuery(
    `(min-height: ${minCollapsableHeight})`
  )

  return (
    <Flex
      flex={{ base: '1', md: '0' }}
      w="100%"
      flexDirection="column"
      textAlign="center"
      justifyContent="flex-end"
      position={{ base: 'static', md: 'absolute' }}
      bottom={{ base: 'auto', md: '0' }}
      display={{
        base: 'block',
        md: tallEnoughForFacepile ? 'block' : 'none'
      }}
      {...boxProps}
    >
      {!isExtension && !isWidget && !isMiniWidget && (
        <>
          <ChakraImage
            w={{ base: '271px', md: '359px' }}
            h={{ base: '46px', md: '46px' }}
            mx="auto"
            src={facePileSrc}
            alt="Clinician testimonials"
          />
          <Text fontSize="12px" color="#282828" pt={2} pb="30px">
            Trusted by 40,000+ mental health clinicians
          </Text>
          <Flex w="100%" alignItems="center" justifyContent="center">
            <HStack spacing={1} mx={2}>
              <ChakraImage src={HIPAAImage} alt="HIPAA Compliant" h="24px" />
              <Text
                fontSize="10px"
                color="#ABBBF6"
                textAlign="left"
                lineHeight="110%"
              >
                HIPAA
                <br />
                Compliant
              </Text>
            </HStack>
            <HStack spacing={1} mx={2}>
              <ChakraImage src={PHIPAImage} alt="PHIPA Compliant" h="24px" />
              <Text
                fontSize="10px"
                color="#ABBBF6"
                textAlign="left"
                lineHeight="110%"
              >
                PHIPA
                <br />
                Compliant
              </Text>
            </HStack>
            <HStack spacing={1} mx={2}>
              <ChakraImage
                src={SOC2Image}
                alt="SOC2 Type 2 Certified"
                h="24px"
              />
              <Text
                fontSize="10px"
                color="#ABBBF6"
                textAlign="left"
                lineHeight="110%"
              >
                SOC2 Type 2<br />
                Certified
              </Text>
            </HStack>
            <HStack spacing={1} mx={2}>
              <ChakraImage
                src={EncryptionImage}
                alt="256-Bit Encryption"
                h="24px"
              />
              <Text
                fontSize="10px"
                color="#ABBBF6"
                textAlign="left"
                lineHeight="110%"
              >
                256-Bit
                <br />
                Encryption
              </Text>
            </HStack>
          </Flex>
        </>
      )}
      {showTerms && termsComponent && <Box pt="14px">{termsComponent}</Box>}
    </Flex>
  )
}

export default SocialProofFooter
